import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  type Buyback,
  type BuybackFinishLine,
  type SlabReading,
  type Location,
  type PosConflict,
  type Product,
  type PaynowReceipt,
  type SaleFlag,
  type SaleSummary,
  api,
  formatMoney,
} from "./api";
import { singaporeDay, singaporeToday } from "./dates";
import { slabLabelDescription } from "./slabLabel";
import { productName } from "./productName";
import { priceForAmount, amountForPrice } from "./linePrice";
import { SelectField } from "./fields";
import { unsureSentence, useReading } from "./reading";
import { TikTokImport } from "./TikTokImport";
import { PageHeader } from "./layout/Layout";
import {
  Actions,
  Alert,
  Field,
  Badge,
  Box,
  Empty,
  IconAction,
  IconLink,
  Loading,
  Modal,
  Pager,
  StatCard,
  Table,
  Th,
  TwoLine,
  usePaged,
  Working,
} from "./ui";
import { productCode } from "./productCode";
import { HoverPreview } from "./HoverPreview";
import { DailySummary } from "./DailySummary";
import { EarlierSale } from "./EarlierSale";
import { shrink } from "./pos/photos";

/**
 * The desk side of the booth (quotation C1 to C5).
 *
 * What the iPads did, once it has reached the server: the sales with who sold them and
 * whether the money came, what was bought and still has to be turned into stock, and the
 * few things the server could not settle by itself, like one slab sold twice.
 */
const MODE_TITLES = {
  all: { title: "Sales and buybacks", subtitle: "All sales from the iPads, the management dashboard and TikTok, and all customer buybacks." },
  retail: { title: "Retail sales", subtitle: "Sales at the shop, and sales keyed at the desk. A place is a shop or a tradeshow booth as set on Inventory, Locations." },
  tradeshow: { title: "Tradeshow sales", subtitle: "Sales at the tradeshow booths: every place set as a tradeshow booth on Inventory, Locations." },
  tiktok: { title: "TikTok sales", subtitle: "Orders imported from TikTok Seller Center." },
  buybacks: { title: "Card buybacks", subtitle: "Cards bought from customers at the booth and the shop, and what became of them." },
} as const;

export function Sales({
  mode = "all",
  canReverse,
  canFinish,
  canVoid,
  canInvoice,
}: {
  /**
   * Which of D's menu items this is (15 October 2026): retail sales at the shop, sales at
   * tradeshow booths, TikTok, the card buybacks, or everything together.
   */
  readonly mode?: "all" | "retail" | "tradeshow" | "tiktok" | "buybacks";
  readonly canReverse: boolean;
  readonly canFinish: boolean;
  readonly canVoid: boolean;
  /** Printing the invoice, which is a supervisor's job and above. */
  readonly canInvoice: boolean;
}): React.ReactElement {
  const [allSales, setSales] = useState<SaleSummary[]>([]);
  const sales = mode === "retail" || mode === "tradeshow" || mode === "tiktok" ? allSales.filter((sale) => (sale.channel ?? "retail") === mode) : allSales;
  const showSales = mode !== "buybacks";
  const showBuybacks = mode === "all" || mode === "buybacks";
  const [buybacks, setBuybacks] = useState<Buyback[]>([]);
  const [conflicts, setConflicts] = useState<PosConflict[]>([]);
  const [day, setDay] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [reversing, setReversing] = useState<SaleSummary | undefined>();
  /** A sale keyed in wrong, to take off on its own day (D, 15 October 2026). */
  const [removing, setRemoving] = useState<SaleSummary | undefined>();
  /** A sale to put together with the other half of its payment (D, 18 October 2026). */
  const [combining, setCombining] = useState<SaleSummary | undefined>();
  /** A sale whose prices are being put right (D, 29 October 2026). */
  const [repricing, setRepricing] = useState<SaleSummary | undefined>();
  const [finishing, setFinishing] = useState<Buyback | undefined>();
  const [importing, setImporting] = useState(false);
  /** What the TikTok import needs to ask: where stock ships from, and which bank TikTok pays into. */
  const [places, setPlaces] = useState<Location[]>([]);
  const [bankAccounts, setBankAccounts] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (!canReverse) return;
    void api.locations().then((result) => setPlaces(result.locations.filter((place) => place.isActive)));
    void api.accounts().then((result) =>
      setBankAccounts(
        result.accounts
          .filter((account) => account.ownerKind === "company" && account.kind === "bank" && account.isActive)
          .map((account) => ({ id: account.id, name: account.name })),
      ),
    );
  }, [canReverse]);
  const [cancelling, setCancelling] = useState<Buyback | undefined>();
  /** A sale keyed in after its day, from photos taken at the time (D, 10 October 2026). */
  const [recordingEarlier, setRecordingEarlier] = useState(false);
  /** The sale a photo is being dragged over, to drop it on. */
  const [dropOn, setDropOn] = useState<string | undefined>();
  async function dropPhotos(sale: SaleSummary, files: FileList): Promise<void> {
    const list = [...files].filter((file) => file.type.startsWith("image/"));
    if (list.length === 0) return;
    let added = 0;
    for (const file of list) {
      try {
        const taken = await shrink(file);
        await api.addSalePhoto(sale.id, { contentType: taken.contentType, data: taken.data });
        URL.revokeObjectURL(taken.preview);
        added += 1;
      } catch {
        // Counted below as not added.
      }
    }
    setNotice(`${added} ${added === 1 ? "photo" : "photos"} added to ${sale.reference}.${added < list.length ? ` ${list.length - added} could not be added.` : ""}`);
    await reload();
  }
  /** Sales put at the wrong location on an iPad, to be moved (D, 9 October 2026). */
  const [relocating, setRelocating] = useState(false);
  /** A buyback saved as cash that was paid by PayNow, or the other way (D, 9 October 2026). */
  const [repaying, setRepaying] = useState<Buyback | undefined>();
  const [photos, setPhotos] = useState<{ title: string; ids: readonly string[] } | undefined>();
  const [downloading, setDownloading] = useState(false);
  /** What the booth has asked a supervisor about. */
  const [flags, setFlags] = useState<SaleFlag[]>([]);
  const [refusing, setRefusing] = useState<SaleFlag | undefined>();
  /** PayNow payments OCBC's alerts reported for the day shown, or today. */
  const [receipts, setReceipts] = useState<PaynowReceipt[]>([]);
  const [matching, setMatching] = useState<PaynowReceipt | undefined>();
  const toCheck = receipts.filter((receipt) => receipt.review).length;

  /*
   * The link in the sales file (D and Daren, 3 October): #/sales?photos=S26A03A28 opens that
   * sale's photographs over the day it was on. Signing in first, when the browser is not,
   * keeps the address, so the link still lands here.
   */
  useEffect(() => {
    function open(): void {
      const reference = new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("photos");
      if (!reference) return;
      void api
        .salePhotos(reference)
        .then((found) => {
          setDay(found.day === singaporeToday() ? "" : found.day);
          if (found.photoIds.length === 0) {
            setError(`${found.reference} has no photos`);
            return;
          }
          setPhotos({ title: `Items sold on ${found.reference}`, ids: found.photoIds });
        })
        .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not open those photos"));
    }
    open();
    window.addEventListener("hashchange", open);
    return () => window.removeEventListener("hashchange", open);
  }, []);

  async function reload(): Promise<void> {
    try {
      const filter = day ? { day } : {};
      const [sold, bought, open, asked, paid] = await Promise.all([
        api.sales(filter),
        api.buybacks(filter),
        canVoid ? api.posConflicts() : Promise.resolve({ conflicts: [] as PosConflict[] }),
        canVoid ? api.saleFlags() : Promise.resolve({ flags: [] as SaleFlag[] }),
        api.paynowReceipts(day ? { day } : {}).catch(() => ({ receipts: [] as PaynowReceipt[] })),
      ]);
      setReceipts(paid.receipts);
      setSales(sold.sales);
      setBuybacks(bought.buybacks);
      setConflicts(open.conflicts);
      setFlags(asked.flags);
      setError(undefined);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load sales and buybacks");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [day]);

  // A day at a time, newest first, when the list holds more than one (D, 10 October 2026).
  const salesDays = useDays(sales, (sale) => sale.saleDate);
  const pagedSales = usePaged(salesDays.rows);
  const waiting = buybacks.filter((buyback) => buyback.status === "open");
  /**
   * A cancelled buyback keeps its reference but is out of the way until asked for (D, 8
   * October 2026: the ones made testing the booth).
   */
  const [showCancelledBuybacks, setShowCancelledBuybacks] = useState(false);
  const cancelledBuybacks = buybacks.filter((buyback) => buyback.status === "void").length;
  const shownBuybacks = showCancelledBuybacks ? buybacks : buybacks.filter((buyback) => buyback.status !== "void");
  const buybackDays = useDays(shownBuybacks, (buyback) => singaporeDay(buyback.boughtAt));
  const pagedBuybacks = usePaged(buybackDays.rows);
  /** The server sends each sale's cost only to those who see costs (D, 9 October 2026). */
  const seesProfit = sales.some((sale) => sale.costCents !== undefined);

  if (loading) return <Loading />;

  const takings = sales
    .filter((sale) => sale.status === "paid")
    .reduce((sum, sale) => sum + sale.totalCents, 0);
  const unpaid = sales.filter((sale) => sale.status === "unpaid");
  const paidOut = buybacks
    .filter((buyback) => buyback.status !== "void")
    .reduce((sum, buyback) => sum + buyback.amountCents, 0);

  return (
    <>
      <PageHeader
        title={MODE_TITLES[mode].title}
        subtitle={MODE_TITLES[mode].subtitle}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canReverse && (mode === "all" || mode === "tiktok") ? (
              <button type="button" className="ti-btn ti-btn-primary-full !mb-0" onClick={() => setImporting(true)}>
                <i className="ri-tiktok-line" /> Import TikTok orders
              </button>
            ) : null}
            <input
              id="sales-day"
              type="date"
              aria-label="Show one day"
              className="form-control !w-auto"
              value={day}
              max={singaporeToday()}
              onChange={(changed) => setDay(changed.target.value)}
            />
            {day ? (
              <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={() => setDay("")}>
                All dates
              </button>
            ) : null}
            {canReverse ? (
              <button type="button" className="ti-btn ti-btn-light !mb-0 whitespace-nowrap" onClick={() => setRecordingEarlier(true)}>
                <i className="ri-history-line" /> Record an earlier sale
              </button>
            ) : null}
            {canVoid ? (
              <button type="button" className="ti-btn ti-btn-light !mb-0 whitespace-nowrap" onClick={() => setRelocating(true)}>
                <i className="ri-map-pin-line" /> Change location
              </button>
            ) : null}
            <button type="button" className="ti-btn ti-btn-light !mb-0 whitespace-nowrap" onClick={() => setDownloading(true)}>
              <i className="ri-download-2-line" /> Download sales report
            </button>
          </div>
        }
      />

      {error ? (
        <Alert tone="danger" onClose={() => setError(undefined)}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert tone="success" onClose={() => setNotice(undefined)}>
          {notice}
        </Alert>
      ) : null}

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xl:col-span-4 md:col-span-6 col-span-12">
          <StatCard
            label={day ? "Paid sales - selected date" : "Paid sales total"}
            value={formatMoney(takings)}
            icon="ti ti-cash"
            tone="primary"
            note={`${sales.length} ${sales.length === 1 ? "sale" : "sales"} in total`}
          />
        </div>
        <div className="xl:col-span-4 md:col-span-6 col-span-12">
          <StatCard
            label="Outstanding customer payments"
            value={formatMoney(unpaid.reduce((sum, sale) => sum + sale.totalCents, 0))}
            icon="ti ti-hourglass"
            tone={unpaid.length ? "warning" : "success"}
            note={unpaid.length ? `${unpaid.length} unpaid ${unpaid.length === 1 ? "invoice" : "invoices"}` : "No outstanding payments"}
            {...(unpaid.length ? { noteTone: "warning" as const } : {})}
          />
        </div>
        <div className="xl:col-span-4 md:col-span-6 col-span-12">
          <StatCard
            label="Customer buybacks"
            value={formatMoney(paidOut)}
            icon="ti ti-hand-move"
            tone={waiting.length ? "warning" : "secondary"}
            note={waiting.length ? `${waiting.length} awaiting inventory entry` : "All added to inventory"}
            {...(waiting.length ? { noteTone: "warning" as const } : {})}
          />
        </div>
      </div>

      {/*
        What the booth asked a supervisor about, so it can be answered from anywhere: at the
        shop, at home, on a phone. Allowed ones are finished on the iPad without a PIN.
      */}
      {flags.length > 0 ? (
        <Box title="Sale correction requests" count={flags.filter((flag) => flag.status === "open").length}>
          <Table
            head={
              <>
                <Th>Sale</Th>
                <Th>Items</Th>
                <Th end>Total</Th>
                <Th>Issue reported</Th>
                <Th>Status</Th>
                <Th end>Action</Th>
              </>
            }
          >
            {flags.map((flag) => (
              <tr key={flag.id} className="border-b border-defaultborder">
                <td>
                  <TwoLine main={flag.reference} sub={`${when(flag.soldAt)} · iPad ${flag.deviceCode} · ${flag.soldBy}`} />
                </td>
                <td className="text-[0.813rem]">
                  {flag.lines.map((line) => `${line.quantity} × ${line.description}`).join(", ")}
                </td>
                <td className="!text-end">
                  <TwoLine
                    main={formatMoney(flag.totalCents)}
                    sub={flag.saleStatus === "unpaid" ? "Unpaid" : flag.paymentMethod ? (METHOD_LABEL[flag.paymentMethod as keyof typeof METHOD_LABEL] ?? flag.paymentMethod) : undefined}
                  />
                </td>
                <td className="max-w-[18rem] whitespace-normal">
                  <TwoLine main={flag.note} sub={`${flag.askedBy}, ${when(flag.askedAt)}`} />
                </td>
                <td>
                  {flag.status === "open" ? (
                    <Badge tone="warning">Pending</Badge>
                  ) : flag.status === "allowed" ? (
                    <TwoLine main={<Badge tone="success">Approved</Badge>} sub={`${flag.decidedBy ?? ""}, to be applied on the iPad`} />
                  ) : flag.status === "refused" ? (
                    <TwoLine main={<Badge tone="light">Rejected</Badge>} sub={flag.decisionNote ?? undefined} />
                  ) : (
                    <Badge tone="light">Corrected</Badge>
                  )}
                </td>
                <td>
                  {flag.status === "open" ? (
                    <Actions>
                      <IconAction
                        icon="ri-check-line"
                        label="Allow the correction"
                        tip="Approve correction"
                        tone="success"
                        onClick={() =>
                          void api
                            .decideSaleFlag(flag.id, true)
                            .then(async () => {
                              setNotice(`Correction for ${flag.reference} approved. The salesperson applies it on the iPad.`);
                              await reload();
                            })
                            .catch((problem: unknown) =>
                              setError(problem instanceof ApiError ? problem.message : "Could not approve the correction"),
                            )
                        }
                      />
                      <IconAction
                        icon="ri-close-line"
                        label="Refuse it, with the reason"
                        tip="Reject claim"
                        tone="danger"
                        onClick={() => setRefusing(flag)}
                      />
                    </Actions>
                  ) : null}
                </td>
              </tr>
            ))}
          </Table>
        </Box>
      ) : null}

      {conflicts.length > 0 ? (
        <Box title="Conflicts requiring review" count={conflicts.length}>
          <Table
            head={
              <>
                <Th>Details</Th>
                <Th>Sale</Th>
                <Th>Date</Th>
              </>
            }
          >
            {conflicts.map((conflict) => (
              <tr key={conflict.id} className="border-b border-defaultborder">
                <td>{conflict.detail}</td>
                <td className="font-semibold">{conflict.reference ?? "-"}</td>
                <td className="text-[#8c9097] dark:text-white/50">{when(conflict.createdAt)}</td>
              </tr>
            ))}
          </Table>
        </Box>
      ) : null}

      {receipts.length > 0 ? (
        <Box
          title={day ? "PayNow payments received - selected date" : "PayNow payments received today"}
          count={receipts.length}
          actions={
            toCheck > 0 ? (
              <Badge tone="warning">
                <i className="ri-error-warning-line me-1" />
                {toCheck} to review
              </Badge>
            ) : null
          }
        >
          {toCheck > 0 ? (
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-3">
              Possible duplicate payment: the same amount was received more than once within 5 minutes. This may be two
              customers or one customer paying twice, so compare each payment with its sale.
            </p>
          ) : null}
          <Table
            head={
              <>
                <Th>Paid at</Th>
                <Th end>Amount</Th>
                <Th>Linked sale</Th>
                <Th end>Action</Th>
              </>
            }
          >
            {receipts.map((receipt) => (
              <tr
                key={receipt.eventId}
                // The mark stays under the pointer too: the table's own hover would paint it out.
                className={`border-b border-defaultborder${receipt.review ? " !bg-warning/10 hover:!bg-warning/20" : ""}`}
              >
                <td>
                  {receipt.review ? (
                    <TwoLine
                      main={
                        <>
                          <i className="ri-error-warning-line text-warning me-1" aria-label="To look at" />
                          {paidMoment(receipt.paidAt)}
                        </>
                      }
                      sub={sameAmount(receipt.review)}
                    />
                  ) : (
                    paidMoment(receipt.paidAt)
                  )}
                </td>
                <td className="!text-end font-semibold">{formatMoney(receipt.amountCents)}</td>
                <td>
                  {receipt.saleReference ? (
                    <TwoLine main={receipt.saleReference} sub={receipt.matchedBy ? `Matched by ${receipt.matchedBy}` : (receipt.matchNote ?? undefined)} />
                  ) : (
                    <Badge tone="warning">Unmatched payment</Badge>
                  )}
                </td>
                <td>
                  {canVoid ? (
                    <Actions>
                      {receipt.saleReference ? (
                        <IconAction
                          icon="ri-link-unlink"
                          label="Undo the match"
                          tip="Undo match"
                          tone="danger"
                          onClick={() =>
                            void api
                              .matchPaynow(receipt.eventId, null)
                              .then(reload)
                              .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not remove the match"))
                          }
                        />
                      ) : (
                        <IconAction icon="ri-link" label="Match it to a sale"
 tip="Match to a sale"
 tone="primary" onClick={() => setMatching(receipt)} />
                      )}
                    </Actions>
                  ) : null}
                </td>
              </tr>
            ))}
          </Table>
        </Box>
      ) : null}

      {showBuybacks ? (
      <Box
        title="Customer buybacks"
        count={shownBuybacks.length}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <DayPager days={buybackDays} />
            {cancelledBuybacks ? (
              <button type="button" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0" onClick={() => setShowCancelledBuybacks(!showCancelledBuybacks)}>
                {showCancelledBuybacks ? "Hide cancelled" : `Show cancelled (${cancelledBuybacks})`}
              </button>
            ) : null}
          </div>
        }
      >
        {shownBuybacks.length === 0 ? (
          <Empty icon="ri-hand-coin-line">
            {day ? "No customer buybacks on this date." : "No customer buybacks yet."}
          </Empty>
        ) : (
          <Table
            head={
              <>
                <Th>Date</Th>
                <Th>Seller</Th>
                <Th end>Amount paid</Th>
                <Th>Reference</Th>
                <Th>Item description</Th>
                <Th>Photos</Th>
                <Th>Purchased by</Th>
                <Th>Status</Th>
                <Th end>Action</Th>
              </>
            }
          >
            {pagedBuybacks.rows.map((buyback) => (
              <tr key={buyback.id} className="border-b border-defaultborder">
                <td>{when(buyback.boughtAt)}</td>
                <td>
                  <TwoLine main={buyback.sellerName} sub={buyback.sellerPhone ?? undefined} />
                </td>
                <td className="!text-end font-semibold">
                  <TwoLine
                    main={formatMoney(buyback.amountCents)}
                    sub={buyback.method === "later" ? "Unpaid" : buyback.method === "cash" ? "Cash" : "PayNow"}
                  />
                </td>
                <td className="font-semibold">{buyback.reference}</td>
                <td className="max-w-[18rem]">{buyback.description}</td>
                <td>
                  {buyback.photos > 0 ? (
                    <HoverPreview src={buyback.photoIds[0] ? `/api/photos/${buyback.photoIds[0]}?preview=1` : null} kind="image">
                    <button
                      type="button"
                      className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0"
                      onClick={() =>
                        setPhotos({ title: `${buyback.reference} from ${buyback.sellerName}`, ids: buyback.photoIds })
                      }
                    >
                      <i className="ri-image-line" /> {buyback.photos}
                    </button>
                    </HoverPreview>
                  ) : (
                    <span className="text-[#8c9097] dark:text-white/50">None</span>
                  )}
                </td>
                <td className="text-[#8c9097] dark:text-white/50">{buyback.boughtByName}</td>
                <td>
                  {buyback.status === "finished" ? (
                    <Badge tone="success">In inventory</Badge>
                  ) : buyback.status === "void" ? (
                    <Badge tone="light">Cancelled</Badge>
                  ) : (
                    <Badge tone="warning">Awaiting inventory</Badge>
                  )}
                </td>
                <td>
                  <Actions>
                    {canFinish && buyback.status === "open" ? (
                      <IconAction
                        icon="ri-inbox-archive-line"
                        label="Say what it is and put it into stock"
                        tip="Add buyback to inventory"
                        tone="primary"
                        onClick={() => setFinishing(buyback)}
                      />
                    ) : null}
                    {canVoid && buyback.status !== "void" && buyback.method !== "later" ? (
                      <IconAction
                        icon="ri-exchange-dollar-line"
                        label={buyback.method === "cash" ? "It was paid by PayNow" : "It was paid in cash"}
                        tip={buyback.method === "cash" ? "Change to PayNow" : "Change to cash"}
                        tone="secondary"
                        onClick={() => setRepaying(buyback)}
                      />
                    ) : null}
                    {canVoid && buyback.status === "open" ? (
                      <IconAction
                        icon="ri-close-circle-line"
                        label="Cancel this buyback"
                        tip="Cancel buyback"
                        tone="danger"
                        onClick={() => setCancelling(buyback)}
                      />
                    ) : null}
                  </Actions>
                </td>
              </tr>
            ))}
          </Table>
        )}
        <Pager
          total={buybackDays.rows.length}
          page={pagedBuybacks.page}
          pages={pagedBuybacks.pages}
          onPage={pagedBuybacks.setPage}
        />
      </Box>
      ) : null}

      {/* Day by day, place by place, for those who see costs (D, 9 October 2026). */}
      {seesProfit && showSales ? <DailySummary sales={sales} /> : null}

      {showSales ? (
      <Box title="Sales" count={salesDays.rows.length} actions={<DayPager days={salesDays} />}>
        {canReverse && sales.length > 0 ? (
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-3">Drag photos from your computer onto a sale to add them to it.</p>
        ) : null}
        {sales.length === 0 ? (
          <Empty icon="ri-store-2-line">{day ? "No sales on this date." : "No sales yet."}</Empty>
        ) : (
          <Table
            head={
              <>
                <Th>Date</Th>
                <Th>Items</Th>
                <Th end>Total</Th>
                {seesProfit ? <Th end>Cost</Th> : null}
                {seesProfit ? <Th end>Profit</Th> : null}
                <Th>Reference</Th>
                <Th>Salesperson</Th>
                <Th>Customer</Th>
                <Th>Payment</Th>
                <Th>Status</Th>
                <Th end>Action</Th>
              </>
            }
          >
            {pagedSales.rows.map((sale) => (
              <tr
                key={sale.id}
                className={`border-b border-defaultborder ${dropOn === sale.id ? "bg-primary/10 outline outline-2 outline-primary" : ""}`}
                // Photos dropped onto a sale go onto it (D, 10 October 2026).
                {...(canReverse
                  ? {
                      onDragOver: (event: React.DragEvent) => {
                        event.preventDefault();
                        setDropOn(sale.id);
                      },
                      onDragLeave: () => setDropOn(undefined),
                      onDrop: (event: React.DragEvent) => {
                        event.preventDefault();
                        setDropOn(undefined);
                        void dropPhotos(sale, event.dataTransfer.files);
                      },
                    }
                  : {})}
              >
                <td>
                  <TwoLine main={when(sale.soldAt)} sub={sale.event ?? undefined} />
                </td>
                <td className="min-w-[14rem] max-w-[20rem] !whitespace-normal">
                  <TwoLine
                    main={sale.lines.map((line) => `${line.quantity} × ${line.description}`).join(", ")}
                    sub={sale.cancelReason ?? undefined}
                  />
                  {sale.photoIds.length > 0 ? (
                    // The photo shown while the pointer rests on it (D, 9 October 2026).
                    <HoverPreview src={`/api/photos/${sale.photoIds[0]}?preview=1`} kind="image">
                      <button
                        type="button"
                        className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0 mt-1"
                        onClick={() => setPhotos({ title: `Items sold on ${sale.reference}`, ids: sale.photoIds })}
                      >
                        <i className="ri-image-line" /> {sale.photoIds.length}
                      </button>
                    </HoverPreview>
                  ) : null}
                </td>
                <td className="!text-end font-semibold">{formatMoney(sale.totalCents)}</td>
                {seesProfit ? (
                  <td className="!text-end text-[#8c9097] dark:text-white/50" title={sale.costUnknownLines ? "A line with no cost price is counted as nothing" : undefined}>
                    {sale.status === "paid" ? `${formatMoney(sale.costCents ?? 0)}${sale.costUnknownLines ? " *" : ""}` : ""}
                  </td>
                ) : null}
                {seesProfit ? (
                  <td className={`!text-end font-semibold ${sale.totalCents - (sale.costCents ?? 0) < 0 ? "text-danger" : "text-success"}`}>
                    {sale.status === "paid" ? formatMoney(sale.totalCents - (sale.costCents ?? 0)) : ""}
                  </td>
                ) : null}
                <td className="font-semibold">{sale.reference}</td>
                <td className="text-[#8c9097] dark:text-white/50">{sale.soldBy}</td>
                <td className="text-[#8c9097] dark:text-white/50">{sale.customerName ?? "-"}</td>
                <td>
                  {sale.paymentMethod ? (
                    <TwoLine
                      main={
                        sale.cashPartCents
                          ? `Cash ${formatMoney(sale.cashPartCents)} + PayNow ${formatMoney(sale.totalCents - sale.cashPartCents)}`
                          : METHOD_LABEL[sale.paymentMethod]
                      }
                      sub={sale.paidTo ?? undefined}
                    />
                  ) : (
                    <span className="text-[#8c9097] dark:text-white/50">Not paid</span>
                  )}
                </td>
                <td>
                  <span className="flex items-center gap-1 flex-wrap">
                    <Badge tone={STATUS_TONE[sale.status]}>{STATUS_LABEL[sale.status]}</Badge>
                    {sale.openConflicts > 0 ? <Badge tone="danger">Review</Badge> : null}
                    {/* Whether the bank has been seen paying a PayNow sale in. Cash is
                        proved by the drawer count, so it says nothing here. */}
                    {sale.reconciliation === "matched" ? (
                      <Badge tone="success">Matched to statement</Badge>
                    ) : sale.reconciliation === "waiting" ? (
                      <Badge tone="warning">Awaiting bank statement</Badge>
                    ) : null}
                  </span>
                </td>
                <td>
                  <Actions>
                    {canInvoice ? (
                      <IconLink
                        icon="ri-printer-line"
                        label="Print the invoice"
                        tip="Print invoice"
                        tone="primary"
                        href={`/api/sales/${sale.id}/invoice.pdf`}
                      />
                    ) : null}
                    {canReverse && sale.status === "paid" ? (
                      <IconAction
                        icon="ri-arrow-go-back-line"
                        label="Take this sale back, with the reason"
                        tone="danger"
                        onClick={() => setReversing(sale)}
                      />
                    ) : null}
                    {canReverse && (sale.status === "paid" || sale.status === "unpaid") && sale.reconciliation !== "matched" ? (
                      <IconAction
                        icon="ri-delete-bin-line"
                        label="Remove: keyed in wrong"
                        tip="Keyed in wrong? Remove it from its day"
                        tone="danger"
                        onClick={() => setRemoving(sale)}
                      />
                    ) : null}
                    {canReverse && (sale.status === "paid" || sale.status === "unpaid") && sale.paymentMethod !== "tiktok" && !sale.cashPartCents && sale.reconciliation !== "matched" ? (
                      <IconAction icon="ri-pencil-line" label="Change the amount" tip="Keyed at the wrong amount? Change it" tone="primary" onClick={() => setRepricing(sale)} />
                    ) : null}
                    {canReverse && sale.status === "reversed" ? (
                      <IconAction
                        icon="ri-arrow-go-forward-line"
                        label="Undo the reversal"
                        tip="Reversed by mistake? Undo it"
                        tone="warning"
                        onClick={() => {
                          if (!window.confirm(`Undo the reversal of ${sale.reference}? It is paid again as before.`)) return;
                          void api
                            .undoReversal(sale.id)
                            .then((result) => {
                              setNotice(result.message);
                              return reload();
                            })
                            .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not undo it"));
                        }}
                      />
                    ) : null}
                    {canReverse && sale.status === "paid" && (sale.paymentMethod === "cash" || sale.paymentMethod === "paynow") && !sale.cashPartCents ? (
                      <IconAction
                        icon="ri-links-line"
                        label="Put together with the other half of its payment"
                        tip="One sale paid partly in cash and partly by PayNow, keyed as two? Put them together"
                        tone="info"
                        onClick={() => setCombining(sale)}
                      />
                    ) : null}
                  </Actions>
                </td>
              </tr>
            ))}
          </Table>
        )}
        <Pager total={salesDays.rows.length} page={pagedSales.page} pages={pagedSales.pages} onPage={pagedSales.setPage} />
      </Box>
      ) : null}

      {photos ? (
        <Photos
          title={photos.title}
          ids={photos.ids}
          onClose={() => {
            setPhotos(undefined);
            // Back to plain Sales, so a reload does not open them again.
            if (window.location.hash.includes("photos=")) window.history.replaceState(null, "", "#/sales");
          }}
        />
      ) : null}

      {importing ? (
        <TikTokImport
          locations={places}
          accounts={bankAccounts}
          onClose={() => setImporting(false)}
          onDone={(message) => {
            setImporting(false);
            setNotice(message);
            void reload();
          }}
        />
      ) : null}

      {repricing ? (
        <Reprice
          sale={repricing}
          onClose={() => setRepricing(undefined)}
          onDone={async (message) => {
            setRepricing(undefined);
            setNotice(message);
            await reload();
          }}
        />
      ) : null}

      {combining ? (
        <Combine
          sale={combining}
          candidates={allSales.filter(
            (other) =>
              other.id !== combining.id &&
              other.status === "paid" &&
              other.saleDate === combining.saleDate &&
              !other.cashPartCents &&
              (other.paymentMethod === "cash" || other.paymentMethod === "paynow") &&
              other.paymentMethod !== combining.paymentMethod,
          )}
          onClose={() => setCombining(undefined)}
          onDone={async (message) => {
            setCombining(undefined);
            setNotice(message);
            await reload();
          }}
        />
      ) : null}

      {removing ? (
        <WithReason
          title={`Remove ${removing.reference}, keyed in wrong`}
          question={`For a sale that never happened as keyed, such as ${formatMoney(removing.totalCents)} keyed instead of the real amount. It comes off ${removing.saleDate}: ${removing.status === "paid" ? `its money off that day's ${removing.paymentMethod === "cash" ? "cash" : "PayNow takings"} and ` : ""}its items back into inventory, as if it had never been keyed. The sale stays on the list as voided, with your reason. Then key the right one in with Key in a past day. A sale the customer returned is reversed instead.`}
          label="Remove sale"
          onClose={() => setRemoving(undefined)}
          onDone={async (reason) => {
            await api.removeSale(removing.id, reason);
            setNotice(`${removing.reference} removed from ${removing.saleDate}.`);
            setRemoving(undefined);
            await reload();
          }}
        />
      ) : null}

      {reversing ? (
        <WithReason
          title={`Reverse sale ${reversing.reference}`}
          question="Reversal undoes the recorded financial and inventory effects of this sale. Enter a reason."
          label="Reverse sale"
          onClose={() => setReversing(undefined)}
          onDone={async (reason) => {
            await api.reverseSale(reversing.id, reason);
            setNotice(`${reversing.reference} reversed.`);
            setReversing(undefined);
            await reload();
          }}
        />
      ) : null}

      {matching ? (
        <MatchPaynow
          receipt={matching}
          onClose={() => setMatching(undefined)}
          onDone={async (reference) => {
            setMatching(undefined);
            setNotice(`The payment of ${formatMoney(matching.amountCents)} is now matched to ${reference}.`);
            await reload();
          }}
        />
      ) : null}

      {refusing ? (
        <WithReason
          title={`Reject request for ${refusing.reference}`}
          question="The sale remains unchanged. Enter a reason so the salesperson understands the decision."
          label="Reject request"
          onClose={() => setRefusing(undefined)}
          onDone={async (reason) => {
            await api.decideSaleFlag(refusing.id, false, reason);
            setNotice(`Request for ${refusing.reference} rejected. The sale remains unchanged.`);
            setRefusing(undefined);
            await reload();
          }}
        />
      ) : null}

      {cancelling ? (
        <WithReason
          title={`Cancel buyback ${cancelling.reference}`}
          question="Enter the reason this buyback should not have been recorded."
          label="Cancel buyback"
          onClose={() => setCancelling(undefined)}
          onDone={async (reason) => {
            await api.voidBuyback(cancelling.id, reason);
            setNotice(`${cancelling.reference} cancelled.`);
            setCancelling(undefined);
            await reload();
          }}
        />
      ) : null}

      {recordingEarlier ? (
        <EarlierSale
          onClose={() => setRecordingEarlier(false)}
          onDone={(message) => {
            setRecordingEarlier(false);
            setNotice(message);
            void reload();
          }}
        />
      ) : null}

      {relocating ? (
        <Relocate
          day={day || singaporeToday()}
          onClose={() => setRelocating(false)}
          onDone={async (message, movedDay) => {
            setRelocating(false);
            setNotice(message);
            if (movedDay !== (day || singaporeToday())) setDay(movedDay === singaporeToday() ? "" : movedDay);
            else await reload();
          }}
        />
      ) : null}

      {repaying ? (
        <WithReason
          title={`Change ${repaying.reference} to ${repaying.method === "cash" ? "PayNow" : "cash"}`}
          question={
            repaying.method === "cash"
              ? `The ${formatMoney(repaying.amountCents)} goes back into the cash count, and the payout waits for its PayNow line on the OCBC statement in Bank reconciliation.`
              : `The ${formatMoney(repaying.amountCents)} comes out of the cash count for that day instead of the bank.`
          }
          label={repaying.method === "cash" ? "Change to PayNow" : "Change to cash"}
          tone="primary"
          onClose={() => setRepaying(undefined)}
          onDone={async (reason) => {
            const result = await api.changeBuybackPayment(repaying.id, repaying.method === "cash" ? "paynow" : "cash", reason);
            setNotice(`${repaying.reference} is now paid by ${repaying.method === "cash" ? "PayNow" : "cash"} from ${result.accountName}.`);
            setRepaying(undefined);
            await reload();
          }}
        />
      ) : null}

      {finishing ? (
        <FinishBuyback
          buyback={finishing}
          onClose={() => setFinishing(undefined)}
          onDone={(lots) => {
            setNotice(`${finishing.reference} added to inventory: ${lots} ${lots === 1 ? "batch" : "batches"}.`);
            setFinishing(undefined);
            void reload();
          }}
        />
      ) : null}

      {downloading ? <SalesFile onClose={() => setDownloading(false)} /> : null}
    </>
  );
}

/** The day before a YYYY-MM-DD day. */
function dayBefore(day: string): string {
  const [year, month, date] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, date - 1)).toISOString().slice(0, 10);
}

/**
 * Some days' sales as a file for Excel, one row per item.
 *
 * D asked for today's and yesterday's for a salesperson, so those are one tap each. The
 * file has the discount as a row of its own, so its Amount column adds up to the takings.
 */
function SalesFile({ onClose }: { readonly onClose: () => void }): React.ReactElement {
  const today = singaporeToday();
  const yesterday = dayBefore(today);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [sellerId, setSellerId] = useState("");
  const [sellers, setSellers] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    void api
      .salesSellers()
      .then((result) => setSellers(result.sellers))
      .catch(() => setSellers([]));
  }, []);

  const quick = [
    { label: "Today", from: today, to: today },
    { label: "Yesterday", from: yesterday, to: yesterday },
    { label: "Yesterday and today", from: yesterday, to: today },
  ];
  const backwards = from > to;

  return (
    <Modal
      open
      title="Download sales report"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Close
          </button>
          <a
            className={`ti-btn ti-btn-primary-full ${backwards ? "pointer-events-none opacity-50" : ""}`}
            href={api.salesFileUrl(from, to, sellerId || undefined)}
            download
            aria-disabled={backwards}
          >
            <i className="ri-download-2-line" /> Download report
          </a>
        </>
      }
    >
      <p className="form-label">Date range</p>
      <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Which days">
        {quick.map((choice) => {
          const chosen = from === choice.from && to === choice.to;
          return (
            <button
              key={choice.label}
              type="button"
              aria-pressed={chosen}
              className={`ti-btn !mb-0 ${chosen ? "ti-btn-primary" : "ti-btn-light"}`}
              onClick={() => {
                setFrom(choice.from);
                setTo(choice.to);
              }}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="sales-file-from" className="form-label">
            Start date
          </label>
          <input
            id="sales-file-from"
            type="date"
            className="form-control"
            value={from}
            max={today}
            onChange={(changed) => setFrom(changed.target.value)}
          />
        </div>
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="sales-file-to" className="form-label">
            End date
          </label>
          <input
            id="sales-file-to"
            type="date"
            className="form-control"
            value={to}
            max={today}
            onChange={(changed) => setTo(changed.target.value)}
          />
        </div>
        <div className="col-span-12">
          <label htmlFor="sales-file-seller" className="form-label">
            Salesperson
          </label>
          <SelectField
            id="sales-file-seller"
            value={sellerId}
            placeholder="All salespeople"
            options={[{ value: "", label: "All salespeople" }, ...sellers.map((person) => ({ value: person.id, label: person.name }))]}
            onChange={setSellerId}
          />
        </div>
      </div>
      {backwards ? (
        <p className="text-[0.75rem] text-danger mt-3 mb-0">The start date is after the end date.</p>
      ) : (
        <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-3 mb-0">
          Each row represents one item and includes the location, salesperson and payment method. Discounts appear on
          the relevant item rows. The Net column totals the amount paid. Voided sales are excluded.
        </p>
      )}
    </Modal>
  );
}

/** How a sale was paid, in the words on the screen. TikTok holds the money until it pays out. */
const METHOD_LABEL: Record<NonNullable<SaleSummary["paymentMethod"]>, string> = {
  cash: "Cash",
  paynow: "PayNow",
  tiktok: "TikTok",
};

const STATUS_LABEL: Record<SaleSummary["status"], string> = {
  unpaid: "Unpaid",
  paid: "Paid",
  void: "Voided",
  reversed: "Reversed",
};

const STATUS_TONE: Record<SaleSummary["status"], "success" | "warning" | "light" | "danger"> = {
  unpaid: "warning",
  paid: "success",
  void: "light",
  reversed: "danger",
};

function when(at: string): string {
  return new Date(at).toLocaleString("en-SG", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** A PayNow payment's moment to the second, as the bank statement has it, in Singapore time. */
function paidMoment(at: string): string {
  return new Date(at).toLocaleString("en-SG", {
    timeZone: "Asia/Singapore",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** "3 of this amount, 12:00 pm to 12:08 pm": the group a marked payment belongs to. */
function sameAmount(review: NonNullable<PaynowReceipt["review"]>): string {
  const clock = (at: string): string =>
    new Date(at).toLocaleTimeString("en-SG", { timeZone: "Asia/Singapore", hour: "2-digit", minute: "2-digit" });
  const span =
    singaporeDay(review.firstAt) === singaporeDay(review.lastAt)
      ? `${clock(review.firstAt)} to ${clock(review.lastAt)}`
      : `${paidMoment(review.firstAt)} to ${paidMoment(review.lastAt)}`;
  return `${review.count} payments of this amount, ${span}`;
}

/** The photographs the booth took, full size, one after another. */
function Photos({
  title,
  ids,
  onClose,
}: {
  readonly title: string;
  readonly ids: readonly string[];
  readonly onClose: () => void;
}): React.ReactElement {
  return (
    <Modal open size="lg" title={title} onClose={onClose}>
      <div className="grid grid-cols-12 gap-4">
        {ids.map((id) => (
          <div key={id} className="md:col-span-6 col-span-12">
            <a href={`/api/photos/${id}`} target="_blank" rel="noreferrer">
              <img
                src={`/api/photos/${id}`}
                alt={title}
                className="w-full rounded-sm border border-defaultborder dark:border-defaultborder/10"
              />
            </a>
          </div>
        ))}
      </div>
      <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-3 mb-0">
        Tap a photo to open it at full size and check the certificate number or grade.
      </p>
    </Modal>
  );
}

/** A reason, then the one thing this does. */
function WithReason({
  title,
  question,
  label,
  tone = "danger",
  onClose,
  onDone,
}: {
  readonly title: string;
  readonly question: string;
  readonly label: string;
  readonly tone?: "danger" | "primary";
  readonly onClose: () => void;
  readonly onDone: (reason: string) => Promise<void>;
}): React.ReactElement {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();

  async function go(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      await onDone(reason.trim());
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "The action could not be completed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`ti-btn ti-btn-${tone}-full`}
            disabled={busy || reason.trim().length === 0}
            onClick={() => void go()}
          >
            {busy ? <Working label="Working" /> : label}
          </button>
        </>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      <p className="text-[#8c9097] dark:text-white/50">{question}</p>
      <label htmlFor="reason" className="form-label">
        Reason
      </label>
      <input
        id="reason"
        type="text"
        className="form-control"
        value={reason}
        onChange={(changed) => setReason(changed.target.value)}
      />
    </Modal>
  );
}

interface DraftLine {
  readonly key: string;
  readonly kind: "product" | "slab";
  readonly productId: string;
  readonly description: string;
  readonly quantity: string;
  readonly price: string;
  readonly unitPrice?: string | undefined;
  /** What the label said, read off a photograph, to be saved with the slab. */
  readonly slab?: SlabReading & { readonly era?: string | null };
  readonly readNote?: string;
}

// Counted, not random: the key becomes part of a field id, and an id has to be something
// a stylesheet or a test can name.
let nextKey = 0;

const SLAB_WORDS: Readonly<Record<string, string>> = {
  certNo: "the certificate number",
  grader: "the grader",
  grade: "the grade",
  gradeDescription: "the grade description",
  cardName: "the card",
  setName: "the set",
  yearReleased: "the year",
  cardNumber: "the card number",
  language: "the language",
  rarity: "the rarity",
};

function blank(kind: "product" | "slab"): DraftLine {
  nextKey += 1;
  return { key: String(nextKey), kind, productId: "", description: "", quantity: "1", price: "" };
}

/**
 * What the photographs show, line by line, and what each line cost.
 *
 * The price paid at the booth is shared out over the lines, and the shares have to add up
 * to it, so the screen keeps the running total in front of whoever is typing. A graded
 * card goes in with the words from the booth and no cert number: it joins the records
 * still to finish, where the cert and grade are typed off the photo.
 */
function FinishBuyback({
  buyback,
  onClose,
  onDone,
}: {
  readonly buyback: Buyback;
  readonly onClose: () => void;
  readonly onDone: (lots: number) => void;
}): React.ReactElement {
  const [lines, setLines] = useState<DraftLine[]>([blank("product")]);
  const [products, setProducts] = useState<Product[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState("");
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const canRead = useReading();
  /** Which of the booth's photographs a reading is taken from. The first, until one is tapped. */
  const [photoId, setPhotoId] = useState<string>(buyback.photoIds[0] ?? "");
  const [readingKey, setReadingKey] = useState<string | undefined>();

  useEffect(() => {
    void api.products().then((result) => setProducts(result.products));
    void api.locations().then((result) => setLocations(result.locations));
  }, []);

  /** Read the label on the chosen photograph into one graded-card line. */
  async function readLabel(key: string): Promise<void> {
    if (!photoId) return;
    setReadingKey(key);
    setProblem(undefined);
    try {
      const { reading } = await api.readSlab({ photoId });
      const words = slabLabelDescription(reading);
      const { unsure, ...details } = reading;
      change(key, {
        description: words || "Graded card, label not readable",
        slab: { ...reading, era: reading.yearRegionEra ?? null },
        readNote: unsureSentence(unsure, SLAB_WORDS) ?? "Read from the photo. The certificate number and grade are saved to the graded card when you save.",
      });
      void details;
    } catch (trouble) {
      setProblem(trouble instanceof ApiError ? trouble.message : "Could not read that photo");
    } finally {
      setReadingKey(undefined);
    }
  }

  const cents = (text: string): number => Math.round(Number(text.replace(/[^\d.]/g, "")) * 100) || 0;
  const shared = useMemo(() => lines.reduce((sum, line) => sum + cents(line.price), 0), [lines]);
  const left = buyback.amountCents - shared;

  function change(key: string, made: Partial<DraftLine>): void {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...made } : line)));
  }

  async function save(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      const ready: BuybackFinishLine[] = lines.map((line) => ({
        ...(line.kind === "slab" ? { isSlab: true } : { productId: line.productId }),
        description: line.description.trim() || buyback.description,
        quantity: line.kind === "slab" ? 1 : Math.max(1, Math.round(Number(line.quantity) || 0)),
        costCents: cents(line.price),
        ...(line.kind === "slab" && line.slab
          ? {
              slab: {
                certNo: line.slab.certNo,
                grader: line.slab.grader,
                grade: line.slab.grade,
                gradeDescription: line.slab.gradeDescription,
                cardName: line.slab.cardName,
                setName: line.slab.setName,
                yearReleased: line.slab.yearReleased,
                cardNumber: line.slab.cardNumber,
                language: line.slab.language,
                rarity: line.slab.rarity,
              },
            }
          : {}),
      }));
      if (ready.some((line) => !line.isSlab && !line.productId)) {
        setProblem("Select a product for each line, or mark it as a graded card");
        return;
      }
      const done = await api.finishBuyback(buyback.id, { locationId: locationId || null, lines: ready });
      onDone(done.lots);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      size="lg"
      title={`Add buyback to inventory - ${buyback.reference}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || left !== 0}
            onClick={() => void save()}
          >
            {busy ? <Working label="Saving" /> : "Add buyback to inventory"}
          </button>
        </>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="mb-1">
            <span className="text-[#8c9097] dark:text-white/50">Item description:</span> {buyback.description}
          </p>
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
            Seller: {buyback.sellerName}
            {buyback.photos > 0 ? `, ${buyback.photos} ${buyback.photos === 1 ? "photo" : "photos"}` : ""}
          </p>
        </div>
        <div className="text-end">
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-1">Purchase amount {formatMoney(buyback.amountCents)}</p>
          <p className={`font-semibold mb-0 ${left === 0 ? "text-success" : "text-warning"}`}>
            {left === 0 ? "Fully allocated" : `${formatMoney(Math.abs(left))} ${left > 0 ? "unallocated" : "over-allocated"}`}
          </p>
        </div>
      </div>

      {buyback.photoIds.length > 0 ? (
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {buyback.photoIds.map((id) => (
            <button
              key={id}
              type="button"
              aria-label={canRead ? "Read from this photo" : "Open this photo"}
              aria-pressed={canRead && photoId === id}
              className="shrink-0 rounded-sm"
              onClick={() => (canRead ? setPhotoId(id) : window.open(`/api/photos/${id}`, "_blank", "noreferrer"))}
            >
              <img
                src={`/api/photos/${id}`}
                alt="What was bought"
                className={`h-28 rounded-sm border-2 ${canRead && photoId === id ? "border-primary" : "border-defaultborder dark:border-defaultborder/10"}`}
              />
            </button>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="finish-place" className="form-label">
            Inventory location
          </label>
          <SelectField
            id="finish-place"
            value={locationId}
            placeholder="Select"
            options={locations.map((location) => ({ value: location.id, label: location.name }))}
            onChange={setLocationId}
          />
        </div>
      </div>

      {lines.map((line) => (
        <div key={line.key} className="grid grid-cols-12 sm:grid-cols-[repeat(14,minmax(0,1fr))] gap-3 items-end mb-3">
          <div className="sm:col-span-3 col-span-12">
            <label htmlFor={`kind-${line.key}`} className="form-label">
              Item type
            </label>
            <SelectField
              id={`kind-${line.key}`}
              value={line.kind}
              options={[
                { value: "product", label: "Sealed product" },
                { value: "slab", label: "Graded card" },
              ]}
              onChange={(value) => change(line.key, { kind: value as DraftLine["kind"], productId: "", quantity: value === "slab" ? "1" : line.quantity, unitPrice: undefined })}
            />
          </div>
          <div className="sm:col-span-4 col-span-12">
            {line.kind === "product" ? (
              <>
                <label htmlFor={`product-${line.key}`} className="form-label">
                  Product
                </label>
                <SelectField
                  id={`product-${line.key}`}
                  value={line.productId}
                  placeholder="Select"
                  options={products.map((product) => ({ value: product.id, label: `${productCode(product)} · ${productName(product)}` }))}
                  onChange={(value) => change(line.key, { productId: value })}
                />
              </>
            ) : (
              <>
                <label htmlFor={`what-${line.key}`} className="form-label">
                  Card description
                </label>
                <div className="flex gap-2">
                  <input
                    id={`what-${line.key}`}
                    type="text"
                    className="form-control"
                    placeholder="Charizard ex, possibly PSA 9"
                    value={line.description}
                    onChange={(changed) => change(line.key, { description: changed.target.value })}
                  />
                  {canRead && buyback.photoIds.length > 0 ? (
                    <button
                      type="button"
                      aria-label="Read the label from the photo"
                      title="Read the label from the photo"
                      className="ti-btn ti-btn-light !mb-0 shrink-0"
                      disabled={readingKey !== undefined}
                      onClick={() => void readLabel(line.key)}
                    >
                      <i className={readingKey === line.key ? "ri-loader-4-line animate-spin" : "ri-camera-lens-line"} />
                    </button>
                  ) : null}
                </div>
                {line.readNote ? (
                  <p className="text-[0.75rem] text-info mt-1 mb-0">{line.readNote}</p>
                ) : null}
              </>
            )}
          </div>
          <div className="sm:col-span-2 col-span-6">
            <label htmlFor={`qty-${line.key}`} className="form-label">
              Quantity
            </label>
            <input
              id={`qty-${line.key}`}
              type="text"
              inputMode="numeric"
              className="form-control"
              disabled={line.kind === "slab"}
              value={line.kind === "slab" ? "1" : line.quantity}
              onChange={(changed) => change(line.key, { quantity: changed.target.value, unitPrice: line.unitPrice ?? priceForAmount(line.price, line.quantity), price: amountForPrice(line.unitPrice ?? priceForAmount(line.price, line.quantity), changed.target.value) })}
            />
          </div>
          <div className="sm:col-span-2 col-span-6">
            <label htmlFor={`unit-${line.key}`} className="form-label">Price S$</label>
            <input id={`unit-${line.key}`} type="text" inputMode="decimal" className="form-control" placeholder="0.00" value={line.unitPrice ?? priceForAmount(line.price, line.quantity)}
              onChange={(event) => change(line.key, { unitPrice: event.target.value, price: amountForPrice(event.target.value, line.quantity) })} />
          </div>
          <div className="sm:col-span-2 col-span-6">
            <label htmlFor={`cost-${line.key}`} className="form-label">
              Line amount S$
            </label>
            <input
              id={`cost-${line.key}`}
              type="text"
              inputMode="decimal"
              className="form-control"
              placeholder="0.00"
              value={line.price}
              onChange={(changed) => change(line.key, { price: changed.target.value, unitPrice: undefined })}
            />
          </div>
          <div className="sm:col-span-1 col-span-12">
            <button
              type="button"
              aria-label="Take this line out"
              className="ti-btn ti-btn-icon ti-btn-light !mb-0"
              disabled={lines.length === 1}
              onClick={() => setLines((current) => current.filter((entry) => entry.key !== line.key))}
            >
              <i className="ri-delete-bin-line" />
            </button>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="ti-btn ti-btn-light !mb-0"
          onClick={() => setLines((current) => [...current, blank("product")])}
        >
          <i className="ri-add-line" /> Add line
        </button>
        <button
          type="button"
          className="ti-btn ti-btn-light !mb-0"
          onClick={() => setLines((current) => [...current, blank("slab")])}
        >
          <i className="ri-award-line" /> Add graded card
        </button>
        {left !== 0 && lines.length > 0 ? (
          <button
            type="button"
            className="ti-btn ti-btn-light !mb-0"
            onClick={() => {
              const last = lines[lines.length - 1];
              if (!last) return;
              change(last.key, { price: ((cents(last.price) + left) / 100).toFixed(2), unitPrice: undefined });
            }}
          >
            Allocate remainder to last line
          </button>
        ) : null}
      </div>
    </Modal>
  );
}

/** A PayNow payment nobody picked, matched to its sale by a person: the same amount, near that day. */
function MatchPaynow({
  receipt,
  onClose,
  onDone,
}: {
  readonly receipt: PaynowReceipt;
  readonly onClose: () => void;
  readonly onDone: (reference: string) => Promise<void>;
}): React.ReactElement {
  const [sales, setSales] = useState<{ id: string; reference: string; paidAt: string }[] | undefined>();
  const [chosen, setChosen] = useState("");
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api
      .paynowCandidates(receipt.eventId)
      .then((result) => setSales(result.sales))
      .catch(() => setSales([]));
  }, [receipt.eventId]);

  return (
    <Modal
      open
      title={`PayNow ${formatMoney(receipt.amountCents)}, ${when(receipt.paidAt)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || !chosen}
            onClick={async () => {
              setBusy(true);
              try {
                await api.matchPaynow(receipt.eventId, chosen);
                await onDone(sales?.find((sale) => sale.id === chosen)?.reference ?? "");
              } catch (trouble) {
                setProblem(trouble instanceof ApiError ? trouble.message : "Could not match the payment");
                setBusy(false);
              }
            }}
          >
            Match payment
          </button>
        </>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      {sales === undefined ? (
        <Loading />
      ) : sales.length === 0 ? (
        <Empty icon="ri-qr-code-line">No unpaid PayNow sale of this amount was found near that date.</Empty>
      ) : (
        <>
          <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
            Unpaid PayNow sales of the same amount, nearest in time first.
          </p>
          <SelectField
            id="paynow-match-sale"
            value={chosen}
            placeholder="Select a sale"
            options={sales.map((sale) => ({ value: sale.id, label: `${sale.reference}, paid ${when(sale.paidAt)}` }))}
            onChange={setChosen}
          />
        </>
      )}
    </Modal>
  );
}

/**
 * Move a day's sales and buybacks from one iPad to the right location (D, 9 October 2026:
 * the iPad was still set to Oxley for the first sales at Junction 8). Only those at the old
 * location move, so the sales made after it was set right are left as they are.
 */
function Relocate({
  day: startDay,
  onClose,
  onDone,
}: {
  readonly day: string;
  readonly onClose: () => void;
  readonly onDone: (message: string, day: string) => Promise<void>;
}): React.ReactElement {
  const [day, setDay] = useState(startDay);
  const [places, setPlaces] = useState<Location[]>([]);
  const [daySales, setDaySales] = useState<SaleSummary[]>([]);
  const [dayBuybacks, setDayBuybacks] = useState<Buyback[]>([]);
  const [device, setDevice] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [event, setEvent] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();

  useEffect(() => {
    void api.locations().then((result) => setPlaces(result.locations.filter((place) => place.isActive)));
  }, []);
  useEffect(() => {
    void Promise.all([api.sales({ day }), api.buybacks({ day })]).then(([sold, bought]) => {
      setDaySales(sold.sales);
      setDayBuybacks(bought.buybacks);
      const codes = [...new Set([...sold.sales.map((sale) => sale.deviceCode), ...bought.buybacks.map((buyback) => buyback.reference.slice(6, 7))])];
      setDevice((current) => (current && codes.includes(current) ? current : (codes[0] ?? "")));
    });
  }, [day]);

  const NONE = "none";
  const ofDevice = daySales.filter((sale) => sale.deviceCode === device);
  // What this iPad's sales that day were put at, with how many each.
  const where = new Map<string, number>();
  for (const sale of ofDevice) where.set(sale.location ?? NONE, (where.get(sale.location ?? NONE) ?? 0) + 1);
  const fromName = from === NONE ? null : (places.find((place) => place.id === from)?.name ?? null);
  const moving = ofDevice.filter((sale) => (sale.location ?? NONE) === (from === NONE ? NONE : fromName));
  const movingBuybacks = dayBuybacks.filter((buyback) => buyback.reference.slice(6, 7) === device).length;

  async function go(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      const result = await api.relocateSales({
        day,
        deviceCode: device,
        fromLocationId: from === NONE ? null : from,
        toLocationId: to,
        ...(event.trim() ? { event: event.trim() } : {}),
        reason: reason.trim(),
      });
      await onDone(
        `${result.sales} ${result.sales === 1 ? "sale" : "sales"}${result.buybacks ? ` and ${result.buybacks} ${result.buybacks === 1 ? "buyback" : "buybacks"}` : ""} moved to ${result.to}.`,
        day,
      );
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "The location could not be changed");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title="Change the location of sales"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || !device || !from || !to || from === to || !reason.trim()}
            onClick={() => void go()}
          >
            {busy ? <Working label="Saving" /> : `Move ${moving.length} ${moving.length === 1 ? "sale" : "sales"}`}
          </button>
        </>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem]">
        For an iPad that was set to the wrong location. The sales and buybacks it made that day at the old location move to the new
        one. Stock is not changed.
      </p>
      <div className="grid grid-cols-12 gap-4">
        <Field id="relocate-day" label="Day" className="sm:col-span-6 col-span-12">
          <input id="relocate-day" type="date" className="form-control" value={day} max={singaporeToday()} onChange={(changed) => setDay(changed.target.value)} />
        </Field>
        <Field id="relocate-device" label="iPad" className="sm:col-span-6 col-span-12">
          <SelectField
            id="relocate-device"
            value={device}
            placeholder="No sales that day"
            options={[...new Set([...daySales.map((sale) => sale.deviceCode), ...dayBuybacks.map((buyback) => buyback.reference.slice(6, 7))])].map((code) => ({ value: code, label: `iPad ${code}` }))}
            onChange={setDevice}
          />
        </Field>
        <Field id="relocate-from" label="Now at" className="sm:col-span-6 col-span-12">
          <SelectField
            id="relocate-from"
            value={from}
            placeholder="Select"
            options={[...where.entries()].map(([name, count]) => ({
              value: name === NONE ? NONE : (places.find((place) => place.name === name)?.id ?? name),
              label: `${name === NONE ? "No location set" : name} (${count} ${count === 1 ? "sale" : "sales"})`,
            }))}
            onChange={setFrom}
          />
        </Field>
        <Field id="relocate-to" label="Move to" className="sm:col-span-6 col-span-12">
          <SelectField
            id="relocate-to"
            value={to}
            placeholder="Select"
            options={places.filter((place) => place.id !== from).map((place) => ({ value: place.id, label: place.name }))}
            onChange={setTo}
          />
        </Field>
        <Field id="relocate-event" label="Event (optional)" className="col-span-12" hint="Left empty, each sale keeps the event it has.">
          <input id="relocate-event" type="text" className="form-control" value={event} placeholder="Junction 8 show" onChange={(changed) => setEvent(changed.target.value)} />
        </Field>
        <Field id="relocate-reason" label="Reason" className="col-span-12">
          <input id="relocate-reason" type="text" className="form-control" value={reason} placeholder="iPad was still set to Oxley" onChange={(changed) => setReason(changed.target.value)} />
        </Field>
      </div>
      {movingBuybacks > 0 && from ? (
        <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-3 mb-0">Buybacks from this iPad at the same location that day move with them.</p>
      ) : null}
    </Modal>
  );
}

interface Days<T> {
  /** The rows of the day shown, or every row when there is only one day. */
  readonly rows: readonly T[];
  readonly days: readonly string[];
  readonly at: number;
  readonly setAt: (at: number) => void;
}

/** A list split into its days, newest first, one day shown at a time. */
function useDays<T>(items: readonly T[], dayOf: (item: T) => string): Days<T> {
  const [at, setAt] = useState(0);
  const days = [...new Set(items.map(dayOf))].sort((a, b) => b.localeCompare(a));
  const shown = Math.min(at, Math.max(0, days.length - 1));
  const rows = days.length > 1 ? items.filter((item) => dayOf(item) === days[shown]) : items;
  return { rows, days, at: shown, setAt };
}

/** Day before and day after, with the day shown between them. Nothing for a single day. */
function DayPager<T>({ days }: { readonly days: Days<T> }): React.ReactElement | null {
  if (days.days.length <= 1) return null;
  const day = days.days[days.at] ?? "";
  const button = "ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0";
  return (
    <div className="flex items-center gap-2">
      <button type="button" className={button} disabled={days.at >= days.days.length - 1} onClick={() => days.setAt(days.at + 1)}>
        <i className="ri-arrow-left-s-line" /> Day before
      </button>
      <span className="text-[0.75rem] font-semibold whitespace-nowrap">
        {new Date(`${day}T12:00:00+08:00`).toLocaleDateString("en-SG", { weekday: "short", day: "numeric", month: "short", timeZone: "Asia/Singapore" })}
      </span>
      <button type="button" className={button} disabled={days.at === 0} onClick={() => days.setAt(days.at - 1)}>
        Day after <i className="ri-arrow-right-s-line" />
      </button>
    </div>
  );
}

/**
 * One sale paid partly in cash and partly by PayNow, keyed as two (D, 18 October 2026: A24 800
 * in cash and A23 by PayNow, "the exact same slab"). The sale this was opened on is kept; the
 * other is voided into it.
 */
function Combine({
  sale,
  candidates,
  onClose,
  onDone,
}: {
  readonly sale: SaleSummary;
  readonly candidates: readonly SaleSummary[];
  readonly onClose: () => void;
  readonly onDone: (message: string) => Promise<void>;
}): React.ReactElement {
  const [otherId, setOtherId] = useState(candidates[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const other = candidates.find((one) => one.id === otherId);
  return (
    <Modal
      open
      title={`Put ${sale.reference} together with another sale`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || !other}
            onClick={() => {
              if (!other) return;
              setBusy(true);
              setError(undefined);
              void api
                .combineSales(sale.id, other.id)
                .then((result) => onDone(result.message))
                .catch((problem: unknown) => {
                  setError(problem instanceof ApiError ? problem.message : "Could not put them together");
                  setBusy(false);
                });
            }}
          >
            {busy ? <Working label="Saving" /> : "Put together"}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <p className="text-[0.813rem]">
        For one sale the customer paid partly in cash and partly by PayNow, keyed as two. {sale.reference} stays, paid in both; the
        other is voided into it, and its bank match and photos come with it.
      </p>
      {candidates.length === 0 ? (
        <p className="mb-0 text-warning">
          No other paid sale on {sale.saleDate} by {sale.paymentMethod === "cash" ? "PayNow" : "cash"}. Show that day on this page first.
        </p>
      ) : (
        <SelectField
          id="combine-other"
          label="The other half"
          value={otherId}
          options={candidates.map((one) => ({
            value: one.id,
            label: `${one.reference}, ${one.paymentMethod === "cash" ? "cash" : "PayNow"} ${formatMoney(one.totalCents)}, ${one.lines.map((line) => line.description).join(", ").slice(0, 60)}`,
          }))}
          onChange={setOtherId}
        />
      )}
      {other ? (
        <p className="text-[0.813rem] mt-3 mb-0 font-semibold">
          {sale.reference} becomes {formatMoney(sale.totalCents + other.totalCents)}:{" "}
          {formatMoney((sale.paymentMethod === "cash" ? sale : other).totalCents)} in cash and{" "}
          {formatMoney((sale.paymentMethod === "paynow" ? sale : other).totalCents)} by PayNow.
        </p>
      ) : null}
    </Modal>
  );
}

/** A sale's prices put right, line by line, with the reason (D, 29 October 2026). */
export function Reprice({
  sale,
  onClose,
  onDone,
}: {
  readonly sale: SaleSummary;
  readonly onClose: () => void;
  readonly onDone: (message: string) => Promise<void>;
}): React.ReactElement {
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries(sale.lines.map((line) => [line.lineId ?? "", (line.lineTotalCents / line.quantity / 100).toFixed(2)])),
  );
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const centsOf = (text: string): number => Math.round(Number(text.replace(/[^\d.]/g, "")) * 100);
  const items = sale.lines.reduce((sum, line) => sum + centsOf(prices[line.lineId ?? ""] ?? "0") * line.quantity, 0);
  return (
    <Modal
      open
      title={`Change the amount of ${sale.reference}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || !reason.trim()}
            onClick={() => {
              setBusy(true);
              setError(undefined);
              void api
                .changeSaleAmounts(
                  sale.id,
                  sale.lines.map((line) => ({ lineId: line.lineId ?? "", unitPriceCents: centsOf(prices[line.lineId ?? ""] ?? "0") })),
                  reason,
                )
                .then((result) => onDone(result.message))
                .catch((problem: unknown) => {
                  setError(problem instanceof ApiError ? problem.message : "Could not change it");
                  setBusy(false);
                });
            }}
          >
            {busy ? <Working label="Saving" /> : "Save"}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <p className="text-[0.813rem]">
        The price of each item. The sale's total, and the {sale.paymentMethod === "cash" ? "cash" : "money"} it brought in on {sale.saleDate}, change with it.
      </p>
      {sale.lines.map((line) => (
        <div key={line.lineId} className="flex items-center gap-2 mb-2">
          <span className="flex-1 text-[0.875rem]">
            {line.quantity} × {line.description}
          </span>
          <input
            type="text"
            inputMode="decimal"
            aria-label={`Price each of ${line.description}`}
            className="form-control !w-[7rem]"
            value={prices[line.lineId ?? ""] ?? ""}
            onChange={(event) => setPrices((was) => ({ ...was, [line.lineId ?? ""]: event.target.value }))}
          />
        </div>
      ))}
      <p className="text-[0.813rem] font-semibold">Items now come to {formatMoney(items)}.</p>
      <label htmlFor="reprice-reason" className="form-label">What was keyed wrong</label>
      <input id="reprice-reason" type="text" className="form-control" placeholder="Keyed 64 instead of 46" value={reason} onChange={(event) => setReason(event.target.value)} />
    </Modal>
  );
}
